import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';

import type { ScanAvailability, ScanMerge } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { ReceiptActionRows } from '@/components/expenses/receipt/ReceiptActionRows';
import {
  ReceiptThumbnailRow,
  failureMessageKey,
} from '@/components/expenses/receipt/ReceiptThumbnailRow';
import { ScanUsePanel } from '@/components/expenses/receipt/ScanUsePanel';

import type { UseReceiptScan } from '@/hooks/useReceiptScan';

type UseRow = ScanMerge['useRows'][number];
type PickIntent = 'scan' | 'attach' | 'replace';

const ALLOWED_EXTENSIONS = [
  'jpeg',
  'jpg',
  'png',
  'webp',
  'heic',
  'heif',
  'pdf',
];
const MAX_BYTES = 10 * 1024 * 1024;

// Android Chrome drops the camera option when `accept` is set.
const isAndroid =
  typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

function isReceiptFile(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return (
    file.type.startsWith('image/') ||
    file.type === 'application/pdf' ||
    ALLOWED_EXTENSIONS.includes(extension)
  );
}

interface ReceiptBlockProps {
  scan: Pick<UseReceiptScan, 'state' | 'machine'>;
  availability: ScanAvailability;
  groupId: string;
  itemsAllowed?: boolean;
  /** The form's label and display strings for a Use row. */
  describeUseRow: (row: UseRow) => {
    label: string;
    mine: string;
    theirs: string;
  };
  onView: (url: string) => void;
}

/**
 * The receipt block on the expense form: the action rows, then the photo row
 * and its Use panel. Absent when scanning is hidden, where the caller keeps
 * today's dropzone (flag off) or shows nothing (queued local row).
 */
export function ReceiptBlock({
  scan,
  availability,
  groupId,
  itemsAllowed,
  describeUseRow,
  onView,
}: ReceiptBlockProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const intent = useRef<PickIntent>('attach');
  const [dragActive, setDragActive] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [confirmingRead, setConfirmingRead] = useState(false);
  // The picked control unmounts as the photo row takes its place; focus
  // follows to the row so a keyboard user does not restart from the top.
  const photoRowRef = useRef<HTMLDivElement>(null);
  const focusPhotoRow = useRef(false);
  const photoRowShown =
    (scan.state.kind === 'saved' ? scan.state.replacing : scan.state)?.kind !==
    'actions';
  useEffect(() => {
    if (!focusPhotoRow.current || !photoRowShown) return;
    focusPhotoRow.current = false;
    photoRowRef.current?.focus();
  }, [photoRowShown, scan.state]);

  if (availability.kind === 'hidden') return null;

  const { state, machine } = scan;
  const shown = state.kind === 'saved' ? (state.replacing ?? state) : state;
  const offline = availability.kind === 'offline';

  // One region that stays mounted, so a reader hears the change: a region
  // that mounts already holding its text is skipped.
  const announcement =
    shown.kind === 'reading'
      ? t('expenses:scan.reading')
      : shown.kind === 'read'
        ? t('expenses:scan.readByAi')
        : shown.kind === 'failed'
          ? t(failureMessageKey(shown.messageKey))
          : '';

  const run = (file: File, how: PickIntent) => {
    if (!isReceiptFile(file)) {
      setFileError(t('common:upload.invalidFileType'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setFileError(t('common:upload.fileSizeError'));
      return;
    }
    setFileError(null);
    focusPhotoRow.current = true;
    if (how === 'scan') void machine.scan(file, groupId);
    else if (how === 'replace') void machine.replace(file, groupId);
    else void machine.attach(file, groupId);
  };

  const read = () => void machine.readWithAi(groupId);
  // One tap would spend a free scan, so a counted Read asks first. The Scan
  // row needs no prompt: its picker is already a deliberate step.
  const onReadWithAi =
    availability.kind === 'counted' ? () => setConfirmingRead(true) : read;

  const pick = (how: PickIntent) => {
    intent.current = how;
    inputRef.current?.click();
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same photo again still fires a change.
    event.target.value = '';
    if (file) run(file, intent.current);
  };

  const canDrop = !offline && !machine.locked();
  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!canDrop) return;
    event.preventDefault();
    setDragActive(true);
  };
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    if (file && canDrop) run(file, 'attach');
  };

  return (
    <div
      data-testid="receipt-block"
      onDragOver={onDragOver}
      onDragLeave={() => setDragActive(false)}
      onDrop={onDrop}
      className={`rounded-xl transition-colors motion-reduce:transition-none ${
        dragActive
          ? 'bg-primary-50 outline-2 outline-dashed outline-primary-500 dark:bg-primary-500/15'
          : ''
      }`}
    >
      <p
        role="status"
        aria-live="polite"
        className="sr-only"
        data-testid="receipt-live-region"
      >
        {announcement}
      </p>
      <input
        ref={inputRef}
        type="file"
        accept={
          isAndroid ? undefined : 'image/*,.heic,.heif,.pdf,application/pdf'
        }
        className="hidden"
        onChange={onFileChange}
        data-testid="receipt-file-input"
      />

      {shown.kind === 'actions' ? (
        <ReceiptActionRows
          availability={availability}
          collapsed={shown.collapsed}
          itemsAllowed={itemsAllowed}
          onScan={() => pick('scan')}
          onAttach={() => pick('attach')}
        />
      ) : (
        <div
          ref={photoRowRef}
          tabIndex={-1}
          data-testid="receipt-photo-row"
          className="rounded-xl focus:outline-none"
        >
          <ReceiptThumbnailRow
            state={shown}
            availability={availability}
            itemsAllowed={itemsAllowed}
            onView={onView}
            onReadWithAi={onReadWithAi}
            onTryAgain={() => void machine.tryAgain(groupId)}
            onReplace={() => pick('replace')}
            onRemove={() => machine.remove()}
            onRestore={() => machine.restore()}
          />
          {shown.kind === 'read' && shown.merge ? (
            <ScanUsePanel
              rows={shown.merge.useRows.map(row => ({
                ...describeUseRow(row),
                field: row.field,
                row,
              }))}
              onUse={row => machine.useRow(row)}
              onKeepMine={() => machine.keepMine()}
            />
          ) : null}
        </div>
      )}

      {availability.kind === 'counted' ? (
        <ConfirmationDialog
          isOpen={confirmingRead}
          onClose={() => setConfirmingRead(false)}
          onConfirm={() => {
            setConfirmingRead(false);
            focusPhotoRow.current = true;
            read();
          }}
          title={t('expenses:scan.confirmReadTitle')}
          description={t('expenses:scan.confirmReadBody', {
            allowance: availability.allowance,
            left: availability.left - 1,
          })}
          confirmText={t('expenses:scan.readWithAi')}
          cancelText={t('common:actions.cancel')}
        />
      ) : null}

      {fileError ? (
        <p
          role="alert"
          className="mt-2 text-sm text-error-600 dark:text-error-400"
        >
          {fileError}
        </p>
      ) : null}
    </div>
  );
}
